import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationBell } from "@/components/NotificationBell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, GraduationCap, FileText, Upload, BarChart3,
  PenLine, LogOut, Menu, TrendingUp, History, AlertTriangle, BookOpen, Trophy, ShieldCheck, Wallet, MessageSquare, KeyRound, CalendarDays, LayoutGrid,
} from "lucide-react";

function ChangePasswordDialog({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (nw.length < 6) return toast.error("New password must be at least 6 characters.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed.");
      setCur(""); setNw(""); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not change password."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="mn max-w-md" data-testid="change-password-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#7C3AED]" /> Change password</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Current password</Label><Input type="password" data-testid="current-password-input" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
          <div><Label>New password</Label><Input type="password" data-testid="new-password-input" value={nw} onChange={(e) => setNw(e.target.value)} placeholder="At least 6 characters" className="mt-1.5 h-11 rounded-xl" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-password-btn" className="rounded-xl ember-gradient font-bold">{busy ? "Saving…" : "Update password"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TUTOR_NAV = [
  { to: "/tutor", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/tutor/students", label: "Students", icon: Users },
  { to: "/tutor/classes", label: "Classes", icon: GraduationCap },
  { to: "/tutor/homework", label: "Homework", icon: FileText },
  { to: "/tutor/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/tutor/courses", label: "Courses", icon: LayoutGrid },
  { to: "/tutor/upload", label: "Upload Homework", icon: Upload },
  { to: "/tutor/results", label: "Results", icon: BarChart3 },
  { to: "/tutor/payments", label: "Payments", icon: Wallet },
  { to: "/tutor/communications", label: "Communications", icon: MessageSquare },
  { to: "/tutor/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/tutor/writing", label: "Writing Feedback", icon: PenLine },
];

const STUDENT_NAV = [
  { to: "/student", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/student/homework", label: "This Week", icon: BookOpen },
  { to: "/student/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/student/progress", label: "Progress", icon: TrendingUp },
  { to: "/student/history", label: "History", icon: History },
  { to: "/student/leaderboard", label: "Leaderboard", icon: Trophy },
  { to: "/student/mistakes", label: "Mistake Bank", icon: AlertTriangle },
  { to: "/student/writing", label: "Writing", icon: PenLine },
];

function NavItems({ items, onNav }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={onNav}
          data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all ${
              isActive
                ? "bg-[#4B2BB8] text-white"
                : "text-[#524B6B] hover:bg-[#F3F0FF] hover:text-[#4B2BB8]"
            }`
          }
        >
          <it.icon className="h-[18px] w-[18px]" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [perms, setPerms] = useState(null);
  const [pwdOpen, setPwdOpen] = useState(false);
  useEffect(() => {
    if (user?.role === "tutor") {
      api.get("/settings/tutor-permissions")
        .then((r) => setPerms(r.data))
        .catch(() => setPerms({ payments: false, communications: false, is_owner: !!user.is_owner }));
    }
  }, [user]);

  const gated = { "/tutor/payments": "payments", "/tutor/communications": "communications" };
  const baseItems = user?.role === "tutor"
    ? TUTOR_NAV.filter((it) => {
        const feat = gated[it.to];
        if (!feat) return true;
        if (user?.is_owner) return true;
        return !!perms?.[feat];
      })
    : STUDENT_NAV;
  const items = user?.is_owner ? [...baseItems, { to: "/tutor/tutors", label: "Tutors", icon: ShieldCheck }] : baseItems;
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");

  const SideContent = ({ onNav }) => (
    <div className="flex h-full flex-col">
      <div className="px-5 py-5">
        <Logo />
      </div>
      <div className="flex items-center justify-between px-4 pb-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EDE9FE] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#5B21B6]">
          {user?.role === "tutor" ? "Tutor View" : "Student View"}
        </span>
        <NotificationBell />
      </div>
      <div className="mt-2 flex-1 overflow-y-auto">
        <NavItems items={items} onNav={onNav} />
      </div>
      <div className="border-t border-[#EEEAF8] p-3">
        <div className="flex items-center gap-3 rounded-xl px-2 py-2">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.avatar_url || undefined} />
            <AvatarFallback className="bg-[#EDE9FE] text-[#5B21B6] font-bold text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold text-[#1E1B2E]">{user?.name}</div>
            <div className="truncate text-xs text-[#948EAB]">{user?.email}</div>
          </div>
        </div>
        <button
          onClick={() => setPwdOpen(true)}
          data-testid="change-password-btn"
          className="mt-1 flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[#524B6B] transition-colors hover:bg-[#F5F3FF] hover:text-[#5B21B6]"
        >
          <KeyRound className="h-[18px] w-[18px]" /> Change password
        </button>
        <button
          onClick={async () => { await logout(); navigate("/login"); }}
          data-testid="logout-btn"
          className="mt-1 flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-[#524B6B] transition-colors hover:bg-[#FFF1F2] hover:text-[#E11D48]"
        >
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
      <aside className="mn fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-[#EEEAF8] bg-white lg:block">
        <SideContent />
      </aside>

      <header className="mn sticky top-0 z-20 flex items-center justify-between border-b border-[#EEEAF8] bg-white/90 px-4 py-3 backdrop-blur-md lg:hidden">
        <Logo />
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="rounded-lg p-2 text-[#5B21B6] hover:bg-[#F5F3FF]" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="mn w-72 p-0">
              <SideContent onNav={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className={`lg:pl-64 ${user?.role === "student" ? "mn" : ""}`}>
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-10 lg:py-8">{children}</div>
      </main>
    </div>
  );
}
